import React, { useState } from "react";
import { toast } from "sonner";
import { CalendarX2, RotateCcw, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

export default function RenewalCard({ status, onChanged }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (status.plan !== "pro" || !["monthly", "annual"].includes(status.billing_cycle) || status.subscription_status === "suspended") return null;
  const end = fmtDate(status.scadenza_abbonamento);

  const call = async (path, msg) => {
    setBusy(true);
    try { await api.post(path); toast.success(msg); setConfirm(false); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
    setBusy(false);
  };

  return (
    <div data-testid="renewal-card" className={`rounded-xl border p-5 shadow-sm mb-4 ${status.cancel_at_period_end ? "border-red-200 bg-red-50/50" : "border-slate-200/80 bg-card"}`}>
      {status.cancel_at_period_end ? (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <CalendarX2 style={{ width: 20, height: 20 }} className="text-red-500 mt-0.5" />
            <div>
              <p className="font-bold text-slate-900" data-testid="renewal-cancelled-text">Rinnovo disdetto: il piano Pro resta attivo fino al {end}</p>
              <p className="text-sm text-slate-600">Dopo questa data passerai automaticamente al piano Starter gratuito. Nessun nuovo addebito.</p>
            </div>
          </div>
          <Button data-testid="resume-renewal-btn" variant="outline" disabled={busy} onClick={() => call("/billing/resume-renewal", "Disdetta annullata: il rinnovo automatico è di nuovo attivo")} className="font-semibold">
            <RotateCcw style={{ width: 15, height: 15 }} className="mr-1.5" /> Annulla disdetta
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-slate-900">Rinnovo automatico attivo</p>
            <p className="text-sm text-slate-500">Prossimo rinnovo il {end}. Puoi disdire quando vuoi: mantieni Pro fino a fine periodo pagato.</p>
          </div>
          <Button data-testid="cancel-renewal-btn" variant="outline" onClick={() => setConfirm(true)} className="font-semibold text-red-600 border-red-200 hover:bg-red-50">
            <CalendarX2 style={{ width: 15, height: 15 }} className="mr-1.5" /> Disdici rinnovo
          </Button>
        </div>
      )}
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="cancel-renewal-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Disdire il rinnovo automatico?</DialogTitle>
            <DialogDescription>
              Il piano Pro resterà attivo con tutti i benefici fino al <strong>{end}</strong>, poi l'account passerà automaticamente al piano Starter gratuito. I tuoi preventivi e dati restano al sicuro.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-500">La disdetta non è un rimborso e non elimina l'account. Per i rimborsi valgono le Condizioni di Rimborso; per cancellare tutti i dati usa "Elimina account" nelle Impostazioni.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>Mantieni il rinnovo</Button>
            <Button data-testid="cancel-renewal-confirm" disabled={busy} onClick={() => call("/billing/cancel-renewal", `Rinnovo disdetto: Pro attivo fino al ${end}`)} className="bg-red-600 hover:bg-red-700 font-semibold">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Conferma disdetta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
